import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import isEmpty from 'lodash-es/isEmpty'
import { usePostConvertQuoteMutation } from 'hooks/mutations/usePostConvertQuoteMutation'
import { usePutAcceptQuoteMutation } from 'hooks/mutations/usePutAcceptQuoteMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ConvertStep } from 'types/walletActions'
import { postConvert } from 'types/wallets'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { handleOnMutationError } from 'utils/reactQuery/onMutationError'
import track from 'utils/tracker'
import {
  convertFormValuesAtom,
  convertFromWalletAtom,
  convertStepAtom,
  convertToWalletAtom,
  quoteAtom,
} from '../atoms/atoms'

export const useConvertReview = () => {
  const setConvertStep = useSetAtom(convertStepAtom)
  const convertToWallet = useAtomValue(convertToWalletAtom)
  const convertFromWallet = useAtomValue(convertFromWalletAtom)
  const convertFormValues = useAtomValue(convertFormValuesAtom)
  const [quote, setQuote] = useAtom(quoteAtom)

  const amplitudeEventOptions = {
    category: AmplitudeEventCategory.MERCHANT_PORTAL,
    action: AmplitudeEventAction.CLICK,
    fromCurrency: convertFromWallet?.currency?.code?.toLowerCase(),
    toCurrency: convertToWallet?.currency?.code?.toLowerCase(),
  }

  // API Requests
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { mutate: convertQuote, isPending: isRefreshingQuote } = usePostConvertQuoteMutation()
  const { mutate: acceptQuote, isPending: isAcceptingQuote } = usePutAcceptQuoteMutation()

  // Helper functions
  const handleRefreshQuote = () => {
    resetRequestError()

    const data: postConvert = {
      from: convertFromWallet?.currency?.code,
      to: convertToWallet?.currency?.code,
      fromWallet: convertFromWallet?.id,
      useMaximum: false,
      useMinimum: false,
      ...(convertFormValues?.reference && { reference: convertFormValues?.reference }),
      ...(!isEmpty(convertToWallet) && { toWallet: convertToWallet?.id }),
      ...(convertFormValues?.changedField === 'toAmount'
        ? { amountOut: convertFormValues?.toAmount?.toString() }
        : { amountIn: convertFormValues?.fromAmount?.toString() }),
    }

    convertQuote(data, {
      onSuccess: response => {
        track.Amp.track(AmplitudeEvent.CONVERT_REFRESH_QUOTE, amplitudeEventOptions)
        setQuote(response)
      },
      onError: (error: BaseErrorResponse) => {
        track.Amp.track(AmplitudeEvent.EOF_CONVERT_REFRESH_QUOTE_ERROR, amplitudeEventOptions)
        handleOnMutationError(error, data, setRequestError, '/v1/quote', 'convertQuote')
      },
    })
  }

  const handleAcceptQuote = () => {
    resetRequestError()
    acceptQuote(quote?.uuid, {
      onSuccess: () => {
        track.Amp.track(AmplitudeEvent.CONVERT_ACCEPT_QUOTE, amplitudeEventOptions)
        setConvertStep(ConvertStep.COMPLETE)
      },
      onError: (error: BaseErrorResponse) => {
        track.Amp.track(AmplitudeEvent.EOF_CONVERT_ACCEPT_QUOTE_ERROR, amplitudeEventOptions)
        handleOnMutationError(
          error,
          quote?.uuid,
          setRequestError,
          '/v1/quote/accept/{quoteId}',
          'acceptQuote'
        )
      },
    })
  }

  return {
    requestError,
    isRefreshingQuote,
    isAcceptingQuote,
    handleRefreshQuote,
    handleAcceptQuote,
  }
}
